import React, { useState, useEffect } from "react";
import Logo from "../assets/logo-applaby.png";
import { Menu, X, ChevronRight, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const CREATOR_URL = "https://creator.applaby.com";
const primaryColor = "#43bd98";

export default function Header() {
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	const navItems = [
		{ label: t("menu.home"), path: "/" },
		{ label: t("menu.features"), hash: "funcionalidades" },
		{ label: t("menu.sectors"), hash: "sectores" },
		{ label: t("menu.examples"), path: "/ejemplos" },
		{ label: t("menu.pricing"), hash: "precios" },
	];

	const desktopItems = navItems.filter((item) => item.label !== t("menu.home"));

	const isActive = (item) => {
		const onHome =
			location.pathname === "/" || location.pathname === "/es";
		if (item.path === "/") {
			return onHome && (!location.hash || location.hash === "#inicio");
		}
		if (item.path) return location.pathname === item.path;
		if (item.hash && onHome) {
			return location.hash === `#${item.hash}`;
		}
		return false;
	};

	const scrollToHash = (hash) => {
		const el = document.getElementById(hash);
		if (!el) return;
		const top = el.getBoundingClientRect().top + window.scrollY - 110;
		window.scrollTo({ top, behavior: "smooth" });
	};

	const handleNavClick = (e, item) => {
		setMenuOpen(false);
		if (item.path === "/") {
			e.preventDefault();
			navigate({ pathname: "/", hash: "" });
			window.scrollTo({ top: 0, behavior: "smooth" });
			return;
		}
		if (!item.hash) return;
		e.preventDefault();
		navigate({ pathname: "/", hash: `#${item.hash}` });
		requestAnimationFrame(() => scrollToHash(item.hash));
	};

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 12);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		setMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		document.body.style.overflow = menuOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [menuOpen]);

	const desktopLinkClass = (item) => {
		const active = isActive(item);
		return `relative text-[13.5px] font-medium tracking-wide transition-colors duration-200 ${
			active ? "text-gray-900" : "text-gray-500 hover:text-gray-900"
		} after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:rounded-full after:bg-primary after:transition-all after:duration-300 ${
			active ? "after:w-full" : "after:w-0 hover:after:w-full"
		}`;
	};

	const renderDesktopLinks = () =>
		desktopItems.map((item) => {
			const className = desktopLinkClass(item);
			const onClick = (e) => handleNavClick(e, item);

			if (item.path) {
				return (
					<Link
						key={item.label}
						to={item.path}
						onClick={onClick}
						className={className}
					>
						{item.label}
					</Link>
				);
			}

			return (
				<a
					key={item.label}
					href={`/#${item.hash}`}
					onClick={onClick}
					className={className}
				>
					{item.label}
				</a>
			);
		});

	return (
		<header
			className={`fixed top-0 left-0 z-[100] w-full font-[Poppins] ${
				menuOpen
					? "border-b border-transparent bg-white shadow-none"
					: `transition-[background,box-shadow,border-color,backdrop-filter] duration-300 ${
							scrolled
								? "border-b border-gray-200/70 bg-white/85 shadow-[0_8px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl"
								: "border-b border-transparent bg-white/70 backdrop-blur-md"
						}`
			}`}
		>
			<div className="relative z-[2] mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-10">
				<a
					href="/"
					className="flex shrink-0 items-center transition-opacity hover:opacity-80"
					aria-label="Applaby"
				>
					<img
						src={Logo}
						alt="Applaby"
						className="h-8 w-auto object-contain sm:h-9"
					/>
				</a>

				<nav className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-8 lg:flex">
					{renderDesktopLinks()}
				</nav>

				<div className="flex items-center gap-2 sm:gap-3">
					<a
						href={CREATOR_URL}
						target="_blank"
						rel="noreferrer"
						className="hidden text-[13.5px] font-medium text-gray-600 transition-colors hover:text-gray-900 lg:inline-flex"
					>
						{t("menu.signIn")}
					</a>

					<a
						href={CREATOR_URL}
						target="_blank"
						rel="noreferrer"
						className="hidden items-center justify-center rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-white shadow-[0_2px_8px_rgba(67,189,152,0.28)] transition-all duration-200 hover:-translate-y-px hover:bg-[#3aad8a] hover:shadow-[0_6px_16px_rgba(67,189,152,0.32)] sm:px-5 sm:py-2.5 sm:text-[13.5px] lg:inline-flex"
					>
						{t("menu.start")}
					</a>

					<button
						type="button"
						onClick={() => setMenuOpen((v) => !v)}
						className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors lg:hidden ${
							menuOpen
								? "border border-gray-200 bg-gray-100"
								: "hover:bg-gray-100"
						}`}
						aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
						aria-expanded={menuOpen}
					>
						{menuOpen ? (
							<X size={20} strokeWidth={1.75} />
						) : (
							<Menu size={22} strokeWidth={1.75} />
						)}
					</button>
				</div>
			</div>

			<AnimatePresence>
				{menuOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15, ease: "easeOut" }}
						className="fixed inset-0 z-[1] flex flex-col bg-white lg:hidden"
					>
						<div
							className="pointer-events-none absolute -right-10 -top-20 h-72 w-72 rounded-full"
							style={{
								background: "rgba(67,189,152,0.12)",
								filter: "blur(80px)",
							}}
						/>
						<div
							className="pointer-events-none absolute -left-16 bottom-20 h-60 w-60 rounded-full"
							style={{
								background: "rgba(67,189,152,0.08)",
								filter: "blur(70px)",
							}}
						/>

						<div className="h-[68px] shrink-0" />

						<div className="relative z-[2] flex min-h-0 flex-1 flex-col px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2">
							<p className="mb-7 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
								{t("menu.eyebrow")}
							</p>

							<nav className="flex min-h-0 flex-1 flex-col overflow-y-auto">
								{navItems.map((item, i) => {
									const active = isActive(item);
									const content = (
										<>
											<span
												className="w-7 text-[11px] font-medium tabular-nums"
												style={{
													color: active
														? primaryColor
														: "rgba(67,189,152,0.7)",
												}}
											>
												{String(i + 1).padStart(2, "0")}
											</span>
											<span
												className="flex-1 text-[1.5rem] font-semibold leading-none tracking-tight"
												style={{
													color: active ? primaryColor : "#111827",
												}}
											>
												{item.label}
											</span>
											<ChevronRight
												size={18}
												color={active ? primaryColor : "#d1d5db"}
											/>
										</>
									);
									const className =
										"flex items-center gap-4 border-b border-gray-100 py-4 no-underline";
									const onClick = (e) => handleNavClick(e, item);

									if (item.path) {
										return (
											<Link
												key={item.label}
												to={item.path}
												onClick={onClick}
												className={className}
											>
												{content}
											</Link>
										);
									}

									return (
										<a
											key={item.label}
											href={`/#${item.hash}`}
											onClick={onClick}
											className={className}
										>
											{content}
										</a>
									);
								})}
							</nav>

							<div className="mt-7 grid gap-3">
								<a
									href={CREATOR_URL}
									target="_blank"
									rel="noreferrer"
									className="flex h-[52px] items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold text-white no-underline shadow-[0_12px_40px_rgba(67,189,152,0.28)]"
									style={{
										background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
									}}
								>
									{t("menu.start")} <ArrowRight size={18} />
								</a>
								<a
									href={CREATOR_URL}
									target="_blank"
									rel="noreferrer"
									className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-[#fafafa] text-sm font-medium text-gray-700 no-underline"
								>
									{t("menu.signIn")}
								</a>
								<p className="mt-1 text-center text-[11px] text-gray-400">
									{t("menu.trust")}
								</p>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
}
